/**
 * English catalogue.
 *
 * Typed as `Messages`, so it cannot drift from the German shape: adding a key
 * to `de.ts` without adding it here fails the type check.
 *
 * Register note: the German copy addresses partners formally ("Sie"). English
 * has no such distinction, so these read as plain professional English rather
 * than a word-for-word transcription. German insurance terms with no English
 * equivalent (Beihilfe, SF-Klasse, §34d GewO) keep the German term.
 */
import type { Messages } from './de.js';

export const en: Messages = {
  common: {
    appName: 'Insurensa',
    adminTitle: 'Insurensa Administration',
    portalTitle: 'Insurensa Partner Portal',
    signOut: 'Sign out',
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    open: 'Open',
    details: 'Details',
    view: 'View',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    loading: 'Loading…',
    unknownError: 'Unknown error',
    reference: 'Reference',
    referenceOf: 'Reference {reference}',
    done: 'Done',
    openCount: '{count} open',
    copyright: 'Copyright © Insurensa {year}',
    choose: 'Please select',
    activate: 'Activate',
    deactivate: 'Deactivate',
    approve: 'Approve',
    decline: 'Decline',
    reason: 'Reason',
    note: 'Note',
    date: 'Date',
    createdAt: 'Received',
    kilometres: '{km} km',
    inLast30Days: '+{count} in 30 days',
  },

  language: {
    label: 'Language',
    switchTo: 'Change language',
  },

  table: {
    copy: 'Copy',
    csv: 'CSV',
    excel: 'Excel',
    pdf: 'PDF',
    print: 'Print',
    search: 'Search:',
    entries: 'entries',
    noEntries: 'No entries',
    noData: 'No data available',
    showing: 'Showing {first} to {last} of {total} entries',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
  },

  fields: {
    firstName: 'First name',
    lastName: 'Last name',
    name: 'Name',
    companyName: 'Company name',
    company: 'Company',
    companyNameOptional: 'Company name (optional)',
    contactPerson: 'Contact person',
    email: 'Email',
    emailAddress: 'Email address',
    contactEmail: 'Contact email',
    loginEmail: 'Sign-in email',
    phone: 'Phone',
    alternatePhone: 'Additional phone number',
    username: 'Username',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    confirmNewPassword: 'Confirm new password',
    role: 'Role',
    registrationNumber: 'Registration number',
    registrationNumberShort: 'Reg. no.',
    streetNumber: 'Street and number',
    postalCode: 'Postcode',
    postalCodeShort: 'Postcode',
    address: 'Address',
    fullAddress: 'Full address',
    city: 'Town',
    state: 'Federal state',
    district: 'District',
    coordinates: 'Coordinates',
    radius: 'Radius',
    distance: 'Distance',
    amount: 'Premium',
    amountEur: 'Premium (EUR)',
    paymentFrequency: 'Payment frequency',
    title: 'Title',
    description: 'Description',
    insuranceLine: 'Product line',
    user: 'User',
    partner: 'Partner',
    offers: 'Offers',
    leads: 'Leads',
    reviews: 'Reviews',
    registeredAt: 'Registered',
    lastActive: 'Last active',
    lastSignIn: 'Last sign-in',
    verified: 'Verified',
    sentAt: 'Sent',
    contact: 'Contact',
  },

  validation: {
    registrationFormat: 'Format: A-BBBB-CCCCC-DD',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordMinChars: 'At least {min} characters',
    passwordRule: 'At least {min} characters, upper and lower case, and one digit.',
    passwordRuleShort: 'Min. {min} characters, upper/lower case, digit.',
    noCitiesFound: 'No towns found',
  },

  admin: {
    nav: {
      dashboard: 'Dashboard',
      partners: 'Partners',
      leads: 'Leads',
      users: 'Users',
      reviews: 'Reviews',
      appReleases: 'App releases',
      team: 'Team',
      auditLog: 'Audit log',
    },

    login: {
      title: 'Insurensa Administration',
      subtitle: 'Please sign in with your account.',
      usernamePlaceholder: 'Username here...',
      passwordPlaceholder: 'Password here...',
      submit: 'Sign in to dashboard',
      submitting: 'Signing in…',
    },

    dashboard: {
      subheading: 'Marketplace overview and outstanding tasks',
      users: 'Users',
      leads: 'Leads',
      offers: 'Offers',
      activePartners: 'Active partners',
      accepted: '{percent}% accepted',
      awaitingReview: '{count} awaiting review',
      leadsByLine: 'Leads by product line',
      openTasks: 'Outstanding tasks',
      reviewRegistrations: 'Review partner registrations',
      moderateReviews: 'Moderate customer reviews',
    },

    partners: {
      subheading: 'Review registrations and manage existing partners',
      empty: 'No partners found',
      exportTitle: 'Partners',
    },

    partnerDetail: {
      masterData: 'Company details',
      noCoordinatesWarning:
        'Without coordinates this partner cannot receive any leads from the radius search.',
      coordinatesMissing: 'Not on file',
      documents: 'Documents',
      noDocuments: 'No documents on file.',
      documentRegistration: 'Registration',
      documentApproval: 'Approval',
      decisionHistory: 'Decision history',
      noDecision: 'No decision taken yet.',
      decision: 'Decision',
      handledBy: 'Handled by',
      activity: 'Activity',
      assignedLeads: 'Assigned leads',
      sentOffers: 'Offers submitted',
      reasonPlaceholder: 'Required when declining',
      reasonHint: 'The reason is sent to the partner by email.',
      approvedNote:
        'Once approved, leads already received within the radius are assigned automatically.',
      approvedFeedback: 'Partner approved. {count} existing lead(s) were assigned.',
      declinedFeedback: 'Partner declined.',
    },

    leads: {
      subheading: 'Every insurance enquiry received',
      empty: 'No leads found',
      exportTitle: 'Leads',
      exportTitleFiltered: 'Leads – {line}',
      guest: 'Guest',
    },

    leadDetail: {
      heading: 'Lead',
      customerAnswers: "Customer's answers",
      noAnswers: 'No answers on file.',
      uploadedDocuments: 'Uploaded documents',
      offersWithCount: 'Offers ({count})',
      noOffers: 'No offers yet',
      overview: 'Overview',
      requestedRadius: 'Requested radius',
      phoneAfterAcceptance: 'Only after acceptance',
      assignedPartners: 'Assigned partners ({count})',
      noAssignedPartners:
        'No partners within the radius. Check whether partners have coordinates on file.',
      viewed: 'Viewed',
      notViewed: 'Not viewed yet',
    },

    users: {
      subheading: 'App customers',
      empty: 'No users found',
      exportTitle: 'Users',
      guest: 'Guest',
      guestNoAccount: 'Guest (no account)',
    },

    userDetail: {
      guestSubheading: 'Guest access, no account',
      accountSubheading: 'User account',
      leadsWithCount: 'Leads ({count})',
      noLeads: 'No leads',
      account: 'Account',
      deleteAccount: 'Delete account',
      deleteExplanation:
        'The account is anonymised and can no longer sign in. Leads and offers are retained for auditability.',
      deleteConfirm: 'Delete permanently',
    },

    reviews: {
      subheading: 'Review customer feedback before it becomes public',
      filterLabel: 'Filter by status',
      emptyForStatus: 'There are no reviews with this status.',
      moderatedBy: 'reviewed by {name}',
      attachment: 'Attachment {index}',
      notePrefix: 'Note: {note}',
      reasonPlaceholder: 'Reason (optional)',
    },

    appReleases: {
      heading: 'App releases',
      subheading: 'Controls the update prompts shown in the mobile app',
      published: 'Published releases',
      platform: 'Platform',
      version: 'Version',
      minimumVersion: 'Minimum version',
      createdAt: 'Created',
      forced: 'Forced',
      empty: 'No releases on file',
      addVersion: 'Add a release',
      minimumVersionHint: 'Older installations are forced to update.',
      releaseNotes: 'Release notes',
      showUpdatePrompt: 'Show update prompt',
      forceUpdate: 'Force update',
      submit: 'Create release',
    },

    team: {
      subheading: 'Administration accounts',
      members: 'Team members',
      disabled: 'Disabled',
      yourself: 'You',
      createAccess: 'Create an account',
      initialPassword: 'Initial password',
      initialPasswordHint: 'At least {min} characters. Must be changed on first sign-in.',
      creating: 'Creating…',
    },

    auditLog: {
      heading: 'Audit log',
      subheading: 'An auditable record of every change',
      timestamp: 'Time',
      action: 'Action',
      entity: 'Object',
      performedBy: 'Performed by',
      ip: 'IP',
      empty: 'No entries',
      entityPartner: 'Partner',
      entityUser: 'User',
      entityReview: 'Review',
      entityOffer: 'Offer',
      entityAdmin: 'Team',
      entityAppRelease: 'App release',
    },
  },

  partner: {
    nav: {
      dashboard: 'Dashboard',
      leads: 'Lead inbox',
      offers: 'My offers',
      reviews: 'My reviews',
      profile: 'Profile',
    },

    access: {
      title: 'Access code',
      subtitle: 'Enter the access code to reach the partner portal.',
      digitLabel: 'Digit {index}',
      submit: 'Continue',
      submitting: 'Checking…',
      help: 'Insurensa provides the code. If you have questions, contact your account manager.',
    },

    login: {
      title: 'Welcome to Insurensa',
      subtitle: 'Please sign in with your account.',
      resetNotice: 'Your password was reset for security reasons.',
      requestNewPassword: 'Request a new password',
      submit: 'Sign in to dashboard',
      submitting: 'Signing in…',
      forgotPassword: 'Forgotten your password?',
      noAccount: 'No partner account yet?',
      register: 'Register',
    },

    register: {
      title: 'Register as a partner',
      subtitle: 'Once approved, you receive leads from your area.',
      sectionContact: 'Contact person',
      sectionReach: 'Contact details',
      sectionAddress: 'Address',
      sectionCredentials: 'Sign-in details',
      sectionDocuments: 'Supporting documents',
      registrationNumberHint: 'Your intermediary register number.',
      phoneHint: 'In +49… format',
      cityHint: 'Determines the area you receive leads from.',
      documentsLabel: 'Supporting documents (PDF)',
      documentsHint:
        'Trade registration, register extract and §34d GewO licence. Maximum 10 MB per file.',
      filesSelected: '{count} file(s) selected: {names}',
      acceptTerms: 'I accept the terms of use and the privacy policy.',
      alreadyRegistered: 'Already registered?',
      submit: 'Submit registration',
      submitting: 'Sending…',
      doneTitle: 'Registration received',
      doneMessage:
        'Thank you. We are reviewing your documents and will be in touch by email. Once approved you can sign in with your registration number.',
      toSignIn: 'Go to sign-in',
    },

    forgotPassword: {
      title: 'Reset your password',
      subtitle: 'We will send you a link to reset your password.',
      sent: 'If an account exists for this email address, we have sent a message. The link is valid for 60 minutes.',
      submit: 'Request link',
      submitting: 'Sending…',
      backToSignIn: 'Back to sign-in',
    },

    resetPassword: {
      title: 'Set a new password',
      invalidLink: 'This link is incomplete or has expired.',
      requestNewLink: 'Request a new link',
      done: 'Your password has been changed. All other sessions were signed out.',
      toSignIn: 'Go to sign-in',
      submit: 'Save password',
    },

    dashboard: {
      subheading: 'Your leads and offers at a glance',
      openLeads: 'Open leads',
      noOfferYet: 'No offer yet',
      totalLeads: 'Leads in total',
      offers: 'Offers',
      offersAccepted: '{count} accepted',
      conversionRate: 'Conversion rate',
      awaitingOfferAlert: 'You have {count} lead(s) without an offer.',
      handleNow: 'Handle them now',
      leadsByLine: 'Leads by product line',
      viewAll: 'View all',
      yourRating: 'Your rating',
      ratingBasis: 'Based on {count} published reviews.',
      noRatings:
        'No published reviews yet. Customers can rate you once they have accepted your offer.',
    },

    leads: {
      heading: 'Lead inbox',
      subheading: 'Enquiries from your area',
      new: 'New',
      myOffer: 'My offer',
      noOffer: 'No offer',
      acceptedWithAmount: 'Accepted · {amount}',
      onlyWithoutOffer: 'Without an offer only',
      empty: 'As soon as a matching lead arrives in your area, it appears here.',
      exportTitle: 'Leads',
    },

    leadDetail: {
      heading: 'Lead',
      backToOverview: 'Back to the inbox',
      customerAnswers: "Customer's answers",
      noAnswers: 'No answers on file.',
      customerUploads: 'Uploaded by the customer',
      editOffer: 'Edit offer',
      makeOffer: 'Submit an offer',
      lockedNotice: 'Your offer of {amount} was accepted and can no longer be changed.',
      sentNotice: 'Offer sent. The customer will be notified.',
      amountPlaceholder: '49.90',
      titlePlaceholder: 'e.g. Comprehensive cover, EUR 300 excess',
      bodyPlaceholder: 'Benefits, conditions and notes on the offer',
      bodyCounter: '{count} / 4000 characters',
      documentLabel: 'Offer document (PDF, optional)',
      submitNew: 'Send offer',
      submitUpdate: 'Update offer',
      submitting: 'Sending…',
      updateNotice: 'A change marks the offer as unread for the customer again.',
      contact: 'Contact',
      customerPhone: "Customer's phone number",
      contactLocked:
        "The customer's contact details are released once your offer has been accepted.",
      request: 'Lead',
      yourOffer: 'Your offer',
    },

    offers: {
      heading: 'My offers',
      subheading: 'Every offer you have submitted',
      offer: 'Offer',
      seen: 'Read',
      notSeen: 'Not yet',
      empty: 'Open a lead to submit your first offer.',
      exportTitle: 'My offers',
    },

    reviews: {
      heading: 'My reviews',
      subheading: 'What customers say about you',
      publishedCount: '{count} published reviews',
      none: 'No published reviews yet. Customers can rate you once they have accepted your offer. Reviews are moderated before publication.',
      empty: 'No reviews',
    },

    profile: {
      heading: 'Profile',
      approved: 'Approved',
      inReview: 'Under review',
      masterData: 'Company details and catchment area',
      cityHint: 'Sets the centre point of your catchment area.',
      radiusLabel: 'Catchment area: {km} km',
      radiusHint:
        'You receive leads from customers within this radius. After saving, matching existing leads are assigned retrospectively.',
      saved: 'Profile saved.',
      savedWithMatches: 'Profile saved. {count} further lead(s) were assigned to you.',
      changePassword: 'Change password',
      changingPassword: 'Changing…',
      passwordChanged: 'Password changed. All sessions were ended — you are being signed out.',
      account: 'Account',
      partnerSince: 'Partner since',
      rating: 'Rating',
      noRating: 'None yet',
      attention: 'Action needed',
      noCoordinatesWarning:
        'No coordinates are on file for your account, so you are not currently receiving leads. Select a postcode and town on the left, then save.',
    },
  },
  marketing: {
    nav: {
      skipToContent: 'Skip to content',
      howItWorks: 'How it works',
      lines: 'Cover',
      forBrokers: 'For brokers',
      partnerLogin: 'Partner login',
      openMenu: 'Open menu',
    },

    hero: {
      eyebrow: 'Insurance offers from brokers near you',
      title: 'Describe what you need. Get offers from brokers in your area.',
      body: 'Tell us once what it is about. Vetted brokers within your area come back with a concrete offer. You decide, in your own time, who you want to talk to.',
      appStore: 'Download on the App Store',
      playStore: 'Get it on Google Play',
      storesPending: 'Store links follow when the app is published.',
      brokerLink: 'Are you a broker?',
    },

    steps: {
      title: 'Three steps to an offer',
      lead: 'Not a comparison calculator and not a call-centre queue — people in your region who write you an offer.',
      oneTitle: 'Send an enquiry',
      oneBody: 'A few questions about what you need, answered in the app. That is enough to get an offer — you do not need an account for it.',
      twoTitle: 'Receive offers',
      twoBody: 'Your enquiry goes to brokers within the radius you chose. They reply with the premium, the payment frequency and what the cover includes.',
      threeTitle: 'Choose',
      threeBody: 'You compare the offers side by side and accept one. Only then does that broker receive your contact details.',
    },

    lines: {
      title: 'What Insurensa covers',
      lead: 'From motor insurance to legal expenses cover — each with the questions a broker actually needs to quote properly.',
    },

    trust: {
      title: 'Why Insurensa',
      privacyTitle: 'Your number stays your number',
      privacyBody: 'Your phone number is released only when you accept an offer. Until then no broker can see it — that is built into the platform, not just promised.',
      localTitle: 'Brokers from your region',
      localBody: 'Offers come from brokers near you, who set their own catchment area. Whoever advises you is within reach.',
      freeTitle: 'Free and without obligation',
      freeBody: 'It costs you nothing, and an enquiry is not a contract. You are free to leave every offer on the table.',
      checkedTitle: 'Vetted partners and moderated reviews',
      checkedBody: 'Every partner is checked against their documents before being approved, and reviews are moderated before they are published.',
    },

    brokers: {
      eyebrow: 'For brokers',
      title: 'Enquiries from your catchment area, without buying leads',
      body: 'You decide the radius you want enquiries from. Matching enquiries appear in your portal, you submit an offer, and the customer decides.',
      pointRadius: 'Set your own radius — from the neighbourhood to the whole district.',
      pointFair: 'Every offer on an enquiry sits side by side. There is no paid placement.',
      pointContact: 'You receive contact details as soon as your offer is accepted.',
      cta: 'Register as a partner',
      login: 'Go to partner login',
      note: 'Approval requires your trade registration, register extract and §34d GewO licence.',
    },

    legal: {
      pendingTitle: 'Content not yet supplied',
      pendingBody: 'This page exists but has no content yet. Operating publicly in Germany requires a complete imprint under §5 TMG and a GDPR privacy notice — both have to be supplied by Insurensa and reviewed legally.',
      backHome: 'Back to the home page',
    },

    footer: {
      tagline: 'Insurance offers from brokers in your region.',
      product: 'Product',
      legal: 'Legal',
      imprint: 'Imprint',
      privacy: 'Privacy',
      terms: 'Terms of use',
      partnerPortal: 'Partner portal',
      rights: 'All rights reserved.',
    },
  },
};
